import { css, html, LitElement, type TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

/**
 * A wrapper element to display a popover.
 * Element in slot 'trigger' will trigger the popover on click.
 * Elements in slot 'content' will display in the popover.
 * The popover can be positioned relative to the trigger.
 */
@customElement('d-popover')
export class DPopover extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }

    #popover {
      border: none;
      padding: 0;
      overflow: visible;
      background: transparent;
    }

    #popover.positioned {
      position: fixed;
      inset: 0;
      margin: auto;
      width: 100vw;
      height: 0;
      font-size: 15px;
      line-height: normal;
    }

    #positioner.positioned {
      position: absolute;
      display: flex;
      flex-direction: row-reverse;
    }
  `;

  @property({ type: Number })
  offset = 0;
  @property({ type: String })
  horizontal: 'left' | 'right' | undefined = undefined;
  @property({ type: String })
  vertical: 'up' | 'down' | undefined = undefined;
  @property({ type: Number, attribute: 'vertical-adjust' })
  verticalAdjust = 0;
  @property({ type: Boolean })
  cover = false;
  @property({ type: Boolean })
  manual = false;
  @property({ type: Number })
  gap = 0;
  @property({ type: String })
  background = 'transparent';
  @property({ type: Number, attribute: 'border-radius' })
  borderRadius = 0;
  @query('#popover')
  popoverElm!: HTMLElement;
  @query('#positioner')
  positioner!: HTMLElement;

  open(e) {
    if (!this.horizontal || !this.vertical) {
      this.popoverElm.showPopover();
    } else {
      const trigger = e.target;
      if (trigger && trigger.dataset.popover !== '' && 'showPopover' in this.popoverElm) {
        const rect = trigger.getBoundingClientRect();
        if (this.horizontal === 'left') {
          let right = window.innerWidth - rect.left + this.gap;
          if (this.cover) {
            right = window.innerWidth - rect.right;
          }
          this.positioner.style.right = Math.round(right) + 'px';
        }
        if (this.horizontal === 'right') {
          let left = rect.left + rect.width + this.gap;
          if (this.cover) {
            left = rect.left;
          }
          this.positioner.style.left = Math.round(left) + 'px';
        }
        if (this.vertical === 'up') {
          const bottom = window.innerHeight / 2 - rect.bottom;
          this.positioner.style.bottom = Math.round(bottom) + this.verticalAdjust + 'px';
        }
        if (this.vertical === 'down') {
          const top = rect.top - window.innerHeight / 2;
          this.positioner.style.top = Math.round(top) + this.verticalAdjust + 'px';
        }
        this.popoverElm.showPopover();
      }
    }
  }

  private get positionClass() {
    return { positioned: this.vertical !== undefined && this.horizontal !== undefined };
  }

  private get popoverStyle() {
    return {
      borderRadius: this.borderRadius + 'px',
      background: this.background,
    };
  }

  close() {
    this.popoverElm.hidePopover();
  }

  render(): TemplateResult<1> {
    return html`
      <slot name="trigger" @click=${(e) => this.open(e)}></slot>
      <div
        popover="${this.manual ? 'manual' : 'auto'}"
        id="popover"
        class=${classMap(this.positionClass)}
        style=${styleMap(this.popoverStyle)}
      >
        <div id="positioner" class=${classMap(this.positionClass)}>
          <slot name="content"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-popover': DPopover;
  }
}
